import { Controller } from "@hotwired/stimulus"
import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { Markdown } from 'tiptap-markdown'
import CharacterCount from '@tiptap/extension-character-count'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { put } from '@rails/request.js'
import { common, createLowlight } from 'lowlight'

import 'highlight.js/styles/vs2015.css'

import debounce from "../../../javascript/utils/debounce";

export default class extends Controller {
  static targets = ["wordCount"]
  static values = {
    initialContent: Object,
    isEditable: { type: Boolean, default: true },
    maxHeight: { type: String, default: 'auto' },
    minHeight: { type: String, default: 'auto' },
    shouldFillHeight: { type: Boolean, default: false },
    shouldFocus: { type: Boolean, default: false },
    updateUrl: String,
  }

  MIN_WORDS_PER_MINUTE = 120
  MAX_WORDS_PER_MINUTE = 200

  editor = undefined

  connect() {
    if (this.hasInitialContentValue){
      const lowlight = createLowlight(common)

      this.editor = new Editor({
        content: this.initialContentValue,
        editorProps: {
          attributes: {
            class: "\
              border \
              border-border-module \
              dark:prose-invert \
              focus:outline-hidden \
              focus:ring-1 \
              max-w-none \
              lg:data-[fill-height='true']:absolute! \
              lg:data-[fill-height='true']:inset-0 \
              lg:max-h-(--rich-text-max-height) \
              min-h-(--rich-text-min-height) \
              overflow-y-auto \
              p-4 \
              pb-10 \
              prose \
              prose-sm \
              ring-border-hover \
              rounded-lg \
              styled-scrollbar \
              w-full \
            ",
            "data-fill-height": this.shouldFillHeightValue,
            style: `
              --rich-text-max-height: ${this.maxHeightValue};
              --rich-text-min-height: ${this.minHeightValue};
            `,
          },
        },
        element: this.element,
        extensions: [
          CharacterCount.configure({
            limit: null,
          }),
          CodeBlockLowlight.configure({
            lowlight,
          }),
          StarterKit.configure({
            heading: {
              levels: [1, 2, 3],
            },
            listItem: {
              HTMLAttributes: {
                class: "[&>p]:m-0 my-0!",
              },
            },
          }),
          Markdown.configure({
            breaks: false,               // New lines (\n) in markdown input are converted to <br>
            bulletListMarker: '-',       // <li> prefix in markdown output
            html: false,                  // Allow HTML input/output
            linkify: false,              // Create links from "https://..." text
            tightListClass: 'tight',     // Add class to <ul> allowing you to remove <p> margins when tight
            tightLists: true,            // No <p> inside <li> in markdown output
            transformCopiedText: false,  // Copied text is transformed to markdown
            transformPastedText: true,  // Allow to paste markdown text in the editor
          }),
          Link.configure({
            HTMLAttributes: {
              class: "cursor-pointer",
            },
          }),
        ],
        onCreate: () => {
          this.updateWordCount()
        },
        onFocus: (editorProps) => {
          const { event: { target } } = editorProps
          target.style.setProperty('--rich-text-max-height', target.clientHeight)
        },
        onUpdate: debounce(async () => {
          this.updateWordCount()
          await this.saveContent()
        }, 800),
        editable: this.isEditableValue,
      })

      if (this.hasShouldFocusValue && this.shouldFocusValue){
        this.editor.commands.focus()
      }
    }
  }

  disconnect(){
    if (this.editor){
      this.editor.destroy()
    }
  }

  async saveContent(){
    if (this.updateUrlValue){
      await put(this.updateUrlValue, {
        contentType: 'application/json',
        body: JSON.stringify({
          rich_text_page: {
            content_json: JSON.stringify(this.editor.getJSON()),
            content_markdown: this.editor.storage.markdown.getMarkdown(),
          },
        }),
      })
    }
  }

  updateWordCount(){
    if (!this.hasWordCountTarget){
      return
    }

    const wordCount = this.editor.storage.characterCount.words()
    if (wordCount){
      this.wordCountTarget.innerText = this.formatWordCount(wordCount)
      this.wordCountTarget.dataset.hasContent = true
    } else {
      this.wordCountTarget.dataset.hasContent = false
    }
  }

  formatWordCount(wordCount){
    let maxTime = Math.round(wordCount / this.MIN_WORDS_PER_MINUTE)
    let minTime = Math.round(wordCount / this.MAX_WORDS_PER_MINUTE)

    const wordCountText = `${wordCount} ${wordCount > 1 ? 'Words' : 'Word'}`

    // Less than a minute
    if (minTime < 1){
      let maxTime = Math.round(wordCount / (this.MIN_WORDS_PER_MINUTE / 60))
      let minTime = Math.round(wordCount / (this.MAX_WORDS_PER_MINUTE / 60))

      if (minTime === maxTime){
        return `${wordCountText} (~${maxTime} sec)` 
      }
      
      return `${wordCountText} (~${minTime}-${maxTime} sec)`
    }

    if (minTime === maxTime){
      return `${wordCountText} (~${maxTime} min)` 
    }

    return `${wordCountText} (~${minTime}-${maxTime} min)`
  }
}
